import React, { useEffect, useRef, useState } from 'react';
import { PrevParagraphIcon, NextParagraphIcon, Replay10Icon, Forward10Icon } from './CourseAudioIcons';

const HIDE_DELAY = 2500;

const IconBtn = ({ onClick, title, primary = false, children }) => (
    <button
        onClick={onClick}
        title={title}
        className={`w-12 h-12 rounded-full flex items-center justify-center transition shadow-md flex-shrink-0 ${
            primary
                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-600/30'
                : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700'
        }`}
    >
        {children}
    </button>
);

/**
 * Sticky audio toolbar at the bottom of the course view (desktop).
 * Behaves like a video player: auto-hides after a short idle, comes back on
 * mouse / touch / key activity, stays visible while the cursor is over it.
 */
const MOVE_THRESHOLD_PX = 4; // micro-movements below this are ignored (trackpad noise on macOS)

const CourseAudioBottomBar = ({
    isPlaying,
    onPlayPause,
    onPrevParagraph,
    onNextParagraph,
    onSeekBackward,
    onSeekForward,
    sidebarOpen = false,
    language = 'fr',
}) => {
    const fr = language === 'fr';
    const [visible, setVisible] = useState(true);
    const hideTimerRef = useRef(null);
    const lastPosRef = useRef({ x: -1, y: -1 });
    const barRef = useRef(null);

    useEffect(() => {
        const clearHide = () => {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
        };

        const armHide = () => {
            clearHide();
            hideTimerRef.current = setTimeout(() => setVisible(false), HIDE_DELAY);
        };

        const isPointerOverBar = (clientX, clientY) => {
            const el = barRef.current;
            if (!el) return false;
            const r = el.getBoundingClientRect();
            return clientX >= r.left && clientX <= r.right
                && clientY >= r.top && clientY <= r.bottom;
        };

        const onMouseMove = (e) => {
            // Ignore micro-movements (trackpad noise on macOS would otherwise
            // keep resetting the timer indefinitely).
            const dx = e.clientX - lastPosRef.current.x;
            const dy = e.clientY - lastPosRef.current.y;
            if (Math.abs(dx) < MOVE_THRESHOLD_PX && Math.abs(dy) < MOVE_THRESHOLD_PX) return;
            lastPosRef.current = { x: e.clientX, y: e.clientY };

            setVisible(true);
            if (isPointerOverBar(e.clientX, e.clientY)) {
                clearHide(); // keep visible while hovering
            } else {
                armHide();
            }
        };

        const onTouch = () => {
            setVisible(true);
            armHide();
        };

        const onKey = () => {
            setVisible(true);
            armHide();
        };

        // Show briefly on state change (transition / first mount), then arm hide.
        setVisible(true);
        armHide();

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('touchstart', onTouch, { passive: true });
        window.addEventListener('keydown', onKey);

        return () => {
            clearHide();
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('touchstart', onTouch);
            window.removeEventListener('keydown', onKey);
        };
    }, []);

    // Center horizontally on the course content area:
    // - Mobile: viewport center (no sidebar)
    // - Desktop sidebar open: shifted right by half the sidebar width (w-72 = 288px → +144px)
    // - Desktop sidebar closed: viewport center
    const horizontalClass = sidebarOpen
        ? 'left-1/2 md:left-[calc(50%+9rem)]'  // 9rem = 144px = half of w-72
        : 'left-1/2';

    return (
        <div
            ref={barRef}
            data-help="course-audio"
            className={`fixed bottom-4 ${horizontalClass} -translate-x-1/2 z-40 transition-all duration-300 ease-out ${
                visible
                    ? 'opacity-100 translate-y-0 pointer-events-auto'
                    : 'opacity-0 translate-y-3 pointer-events-none'
            }`}
        >
            <div className="flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-white/60 dark:bg-black/40 backdrop-blur-md shadow-xl shadow-black/10 dark:shadow-black/40 border border-gray-200/60 dark:border-white/10">
                {/* Play / Pause */}
                <IconBtn
                    onClick={onPlayPause}
                    title={isPlaying ? (fr ? 'Pause' : 'Pause') : (fr ? 'Lecture' : 'Play')}
                    primary
                >
                    {isPlaying ? (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <rect x="6" y="4" width="4" height="16" rx="1" />
                            <rect x="14" y="4" width="4" height="16" rx="1" />
                        </svg>
                    ) : (
                        <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    )}
                </IconBtn>

                {/* Prev paragraph */}
                <IconBtn onClick={onPrevParagraph} title={fr ? 'Paragraphe précédent' : 'Previous paragraph'}>
                    <PrevParagraphIcon className="w-8 h-8" />
                </IconBtn>

                {/* Next paragraph */}
                <IconBtn onClick={onNextParagraph} title={fr ? 'Paragraphe suivant' : 'Next paragraph'}>
                    <NextParagraphIcon className="w-8 h-8" />
                </IconBtn>

                {/* Seek -10s */}
                <IconBtn onClick={onSeekBackward} title="-10s">
                    <Replay10Icon className="w-8 h-8" />
                </IconBtn>

                {/* Seek +10s */}
                <IconBtn onClick={onSeekForward} title="+10s">
                    <Forward10Icon className="w-8 h-8" />
                </IconBtn>
            </div>
        </div>
    );
};

export default CourseAudioBottomBar;
